import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatFormFieldModule } from '@angular/material/form-field';
import { DragDropModule, CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { ReplaySubject, Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BibliothequesService } from 'src/services/bibliotheques.service';
import { BibliothequePilotageDetailModel } from 'src/models/pilotage/bibliothequePilotageDetailModel';
import { PackageCardComponent } from '../package-card/package-card.component';
import { ScormPackageModel } from 'src/models/scormPackageModel';
import { AssociationModel, ContexteAssignationModel, EntiteContexteAssignationModel, RegroupementModel } from 'src/models/pilotage/contexteAssignationModel ';

@Component({
    selector: 'app-create-edit-bibliotheque',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatSelectModule,
        MatProgressSpinnerModule,
        TranslateModule,
        NgxMatSelectSearchModule,
        MatFormFieldModule,
        DragDropModule,
        MatIconModule,
        PackageCardComponent
    ],
    templateUrl: './create-edit-bibliotheque.component.html'
})
export class CreateEditBibliothequeComponent implements OnInit, OnDestroy {
  isLoading = true;
  isCreationMode: boolean;
  bibliothequeId: number | null = null;

  supportedLanguages = [
    { code: 'fr', label: 'LIBRARIES_MANAGEMENT.NAME_FR' },
    { code: 'en', label: 'LIBRARIES_MANAGEMENT.NAME_EN' },
    { code: 'es', label: 'LIBRARIES_MANAGEMENT.NAME_ES' }
  ];

  entites: EntiteContexteAssignationModel[] = [];
  regroupements: RegroupementModel[] = [];
  associations: AssociationModel[] = [];
  bibliotheque: BibliothequePilotageDetailModel = {} as BibliothequePilotageDetailModel;
  packagesInBibliotheque: ScormPackageModel[] = [];
  availablePackages: ScormPackageModel[] = [];

  contexteAssignation!: ContexteAssignationModel;
  selectedEntites: EntiteContexteAssignationModel[] = [];
  selectedRegroupements: RegroupementModel[] = [];
  selectedAssociations: AssociationModel[] = [];

  entiteFilterCtrl = new FormControl();
  regroupementFilterCtrl = new FormControl();
  associationFilterCtrl = new FormControl();

  filteredEntitesSearch = new ReplaySubject<EntiteContexteAssignationModel[]>(1);
  filteredRegroupementsSearch = new ReplaySubject<RegroupementModel[]>(1);
  filteredAssociationsSearch = new ReplaySubject<AssociationModel[]>(1);

  searchInLibrary: string = '';
  searchAvailable: string = '';
  private _allPackagesInLibrary: ScormPackageModel[] = [];
  private _allAvailablePackages: ScormPackageModel[] = [];

  protected _onDestroy = new Subject<void>();

  constructor(
    private bibliothequeService: BibliothequesService,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService
  ) {
    const id = this.route.snapshot.paramMap.get('id');
    this.bibliothequeId = id ? Number(id) : null;
    this.isCreationMode = !this.bibliothequeId;
  }

  ngOnInit() {
    this.loadInitialData();
    this.setupFilterSubscriptions();
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  private initializeLocales() {
    if (!this.bibliotheque.locales) {
      this.bibliotheque.locales = {};
    }

    this.supportedLanguages.forEach(lang => {
      if (!this.bibliotheque.locales[lang.code]) {
        this.bibliotheque.locales[lang.code] = '';
      }
    });
  }

  private loadInitialData() {
    if (this.isCreationMode) {
      this.initializeLocales();

      this.bibliothequeService.obtenirContexteBibliotheque().subscribe(contexte => {
        this.contexteAssignation = contexte;
        this.availablePackages = this.sortPackagesAlphabetically(contexte.packages);
        this._allAvailablePackages = this.sortPackagesAlphabetically(contexte.packages);
        this.initializeFilters();
        this.isLoading = false;
      });
    } else if (this.bibliothequeId) {
      forkJoin({
        contexte: this.bibliothequeService.obtenirContexteBibliotheque(),
        bibliotheque: this.bibliothequeService.obtenirBibliotheque(this.bibliothequeId)
      }).subscribe(({ contexte, bibliotheque }) => {
        this.contexteAssignation = contexte;
        this.bibliotheque = bibliotheque;

        this.initializeLocales();

        this.packagesInBibliotheque = bibliotheque.scormPackages;
        this._allPackagesInLibrary = [...bibliotheque.scormPackages];

        const filtered = contexte.packages.filter(p => !bibliotheque.scormPackages.some(bp => bp.scormPackageId === p.scormPackageId));
        this.availablePackages = this.sortPackagesAlphabetically(filtered);
        this._allAvailablePackages = this.sortPackagesAlphabetically(filtered);

        this.initializeAssignments();
        this.initializeFilters();
        this.isLoading = false;
      });
    }
  }

  private initializeAssignments() {
    this.selectedEntites = this.contexteAssignation.entites
      .filter((e: EntiteContexteAssignationModel) => this.bibliotheque.novoEntiteIds.includes(e.novoEntiteId));
    this.selectedRegroupements = this.contexteAssignation.regroupements
      .filter((r: RegroupementModel) => this.bibliotheque.novoRegroupementIds.includes(r.regroupementId));
    this.selectedAssociations = this.contexteAssignation.associations
      .filter((a: AssociationModel) => this.bibliotheque.novoEntiteIds.includes(a.novoEntiteId));
  }

  private setupFilterSubscriptions() {
    this.entiteFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => this.filterEntites());

    this.regroupementFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => this.filterRegroupements());

    this.associationFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => this.filterAssociations());
  }

  private initializeFilters() {
    this.filterEntites();
    this.filterRegroupements();
    this.filterAssociations();
  }

  private filterEntites() {
    const filteredEntites = this.contexteAssignation.entites.filter((entite: EntiteContexteAssignationModel) =>
      !this.selectedEntites.some(selected => selected.novoEntiteId === entite.novoEntiteId)
    );
    if (!filteredEntites) {
      return;
    }
    let search = this.entiteFilterCtrl.value;
    if (!search) {
      this.filteredEntitesSearch.next(filteredEntites.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    this.filteredEntitesSearch.next(filteredEntites.filter((entite: EntiteContexteAssignationModel) => entite.nom.toLocaleLowerCase().indexOf(search) > -1));
  }

  private filterRegroupements() {
    const filteredRegroupements = this.contexteAssignation.regroupements.filter((regroupement: RegroupementModel) =>
      !this.selectedRegroupements.some(selected => selected.regroupementId === regroupement.regroupementId)
    );
    if (!filteredRegroupements) {
      return;
    }
    let search = this.regroupementFilterCtrl.value;
    if (!search) {
      this.filteredRegroupementsSearch.next(filteredRegroupements.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    this.filteredRegroupementsSearch.next(filteredRegroupements.filter((regroupement: RegroupementModel) => regroupement.nomLong.toLocaleLowerCase().indexOf(search) > -1 || regroupement.nomCourt.toLocaleLowerCase().indexOf(search) > -1));
  }

  private filterAssociations() {
    const filteredAssociations = this.contexteAssignation.associations.filter((association: AssociationModel) =>
      !this.selectedAssociations.some(selected => selected.novoEntiteId === association.novoEntiteId)
    );
    this.filteredAssociationsSearch.next(filteredAssociations);
    if (!filteredAssociations) {
      return;
    }
    let search = this.associationFilterCtrl.value;
    if (!search) {
      this.filteredAssociationsSearch.next(filteredAssociations.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    this.filteredAssociationsSearch.next(filteredAssociations.filter((association: AssociationModel) => association.denomination.toLocaleLowerCase().indexOf(search) > -1));
  }

  onEntiteSelected(event: any) {
    const entite = this.contexteAssignation.entites.find((e: EntiteContexteAssignationModel) => e.novoEntiteId === event.value);
    if (entite) {
      this.selectedEntites.push(entite);
      this.entiteFilterCtrl.setValue(null);
      this.filterEntites();
    }
  }

  onRegroupementSelected(event: any) {
    const regroupement = this.contexteAssignation.regroupements.find((r: RegroupementModel) => r.regroupementId === event.value);
    if (regroupement) {
      this.selectedRegroupements.push(regroupement);
      this.regroupementFilterCtrl.setValue(null);
      this.filterRegroupements();
    }
  }

  onAssociationSelected(event: any) {
    const association = this.contexteAssignation.associations.find((a: AssociationModel) => a.novoEntiteId === event.value);
    if (association) {
      this.selectedAssociations.push(association);
      this.associationFilterCtrl.setValue(null);
      this.filterAssociations();
    }
  }

  removeEntite(entiteId: number) {
    this.selectedEntites = this.selectedEntites.filter(e => e.novoEntiteId !== entiteId);
    this.filterEntites();
  }

  removeRegroupement(regroupementId: number) {
    this.selectedRegroupements = this.selectedRegroupements.filter(r => r.regroupementId !== regroupementId);
    this.filterRegroupements();
  }

  removeAssociation(entiteId: number) {
    this.selectedAssociations = this.selectedAssociations.filter(a => a.novoEntiteId !== entiteId);
    this.filterAssociations();
  }

  onDrop(event: CdkDragDrop<ScormPackageModel[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      const draggedItem = event.previousContainer.data[event.previousIndex];

      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );

      if (event.container.data === this.packagesInBibliotheque) {
        if (!this._allPackagesInLibrary.some(p => p.scormPackageId === draggedItem.scormPackageId)) {
          this._allPackagesInLibrary.push(draggedItem);
        }
        this._allAvailablePackages = this._allAvailablePackages.filter(p => p.scormPackageId !== draggedItem.scormPackageId);
        this.availablePackages = this.sortPackagesAlphabetically(this.availablePackages);
      } else {
        if (!this._allAvailablePackages.some(p => p.scormPackageId === draggedItem.scormPackageId)) {
          this._allAvailablePackages.push(draggedItem);
        }
        this._allPackagesInLibrary = this._allPackagesInLibrary.filter(p => p.scormPackageId !== draggedItem.scormPackageId);
        this.availablePackages = this.sortPackagesAlphabetically(this.availablePackages);
      }
    }
  }

  filterPackagesInLibrary() {
    if (!this.searchInLibrary.trim()) {
      this.packagesInBibliotheque = [...this._allPackagesInLibrary];
      return;
    }

    const inactivePrefix = this.translate.instant('LIBRARIES_MANAGEMENT.INACTIVE_PREFIX').toLowerCase();

    this.packagesInBibliotheque = this._allPackagesInLibrary.filter(pkg => {
      const locales = pkg.locales?.reduce((acc, locale) => {
        acc[locale.langue] = locale.titre || '';
        return acc;
      }, {} as { [key: string]: string }) || {};

      let name = this.bibliothequeService.getNomLocalise(locales);

      if (!pkg.actif) {
        name = inactivePrefix + name;
      }

      return name.toLowerCase().includes(this.searchInLibrary.toLowerCase());
    });
  }

  filterAvailablePackages() {
    if (!this.searchAvailable.trim()) {
      this.availablePackages = this.sortPackagesAlphabetically(this._allAvailablePackages);
      return;
    }

    const inactivePrefix = this.translate.instant('LIBRARIES_MANAGEMENT.INACTIVE_PREFIX').toLowerCase();

    const filtered = this._allAvailablePackages.filter(pkg => {
      const locales = pkg.locales?.reduce((acc, locale) => {
        acc[locale.langue] = locale.titre || '';
        return acc;
      }, {} as { [key: string]: string }) || {};

      let name = this.bibliothequeService.getNomLocalise(locales);

      if (!pkg.actif) {
        name = inactivePrefix + name;
      }

      return name.toLowerCase().includes(this.searchAvailable.toLowerCase());
    });

    this.availablePackages = this.sortPackagesAlphabetically(filtered);
  }

  clearPackages() {
    this._allAvailablePackages = this.sortPackagesAlphabetically([...this._allAvailablePackages, ...this._allPackagesInLibrary]);
    this.availablePackages = this.sortPackagesAlphabetically([...this._allAvailablePackages]);

    this.packagesInBibliotheque = [];
    this._allPackagesInLibrary = [];

    this.filterAvailablePackages();
  }

  isValid(): boolean {
    return Boolean(
      this.hasTitle() &&
      this.selectedEntites.length + this.selectedRegroupements.length + this.selectedAssociations.length > 0
    );
  }

  save() {
    if (!this.isValid()) return;

    const baseModel = {
      locales: this.bibliotheque.locales || {},
      novoEntiteIds: [
        ...this.selectedEntites.map(e => e.novoEntiteId),
        ...this.selectedAssociations.map(a => a.novoEntiteId)
      ],
      novoRegroupementIds: this.selectedRegroupements.map(r => r.regroupementId),
      scormPackageIds: this._allPackagesInLibrary.map(p => p.scormPackageId)
    };

    const action = this.isCreationMode ?
      this.bibliothequeService.creerBibliotheque({
        ...baseModel,
        creePar: 0
      }) :
      this.bibliothequeService.modifierBibliotheque({
        ...baseModel,
        bibliothequeId: this.bibliothequeId!
      });

    action.subscribe(() => {
      this.router.navigate(['/bibliotheques-managment']);
    });
  }

  cancel() {
    this.router.navigate(['/bibliotheques-managment']);
  }

  hasTitle(): boolean {
    if (!this.bibliotheque.locales) {
      return false;
    }

    return Object.values(this.bibliotheque.locales).some(nom => nom && nom.trim());
  }

  getLocaleKeys(): string[] {
    return Object.keys(this.bibliotheque.locales || {});
  }

  getLanguageLabel(langCode: string): string {
    const lang = this.supportedLanguages.find(l => l.code === langCode);
    return lang ? this.translate.instant(lang.label) : langCode.toUpperCase();
  }

  private getDisplayName(pkg: ScormPackageModel): string {
    const locales = pkg.locales?.reduce((acc, locale) => {
      acc[locale.langue] = locale.titre || '';
      return acc;
    }, {} as { [key: string]: string }) || {};

    let name = this.bibliothequeService.getNomLocalise(locales);

    if (!pkg.actif) {
      const inactivePrefix = this.translate.instant('LIBRARIES_MANAGEMENT.INACTIVE_PREFIX');
      name = inactivePrefix + name;
    }

    return name;
  }

  private sortPackagesAlphabetically(packages: ScormPackageModel[]): ScormPackageModel[] {
    return [...packages].sort((a, b) => {
      const nameA = this.getDisplayName(a).toLowerCase();
      const nameB = this.getDisplayName(b).toLowerCase();
      return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
    });
  }
}
